import { useTranslation } from 'next-i18next'

import { useGetLeaderboardDataQuery } from '~/Leaderboard/apis/leaderboard-api'

import ResponsiveImage from '~/components/Image/ResponsiveImage'

import useCurrentUser from '~/hooks/useCurrentUser'

import { DEFAULT_AVATAR_URL } from '~/shared/constants/assets.const'
import Button from '~/components/Buttons/Button'
import ROUTES from '~/shared/constants/routes.const'
import { getLeagueName } from '~/Leaderboard/utils/utils'
import { useAppSelector } from '~/redux/hooks'
import { isAuthenticated, selectUserIsSchoolTeacher } from '~/auth/slices/auth.slice'

function UserPortrait({
  user,
  rank,
  className,
}: {
  user: { id: number; nickname: string; avatarPngUrl: string; xp: number }
  rank: number
  className?: string
}): React.ReactElement {
  const defaultAvatarPng = DEFAULT_AVATAR_URL

  return (
    <div className={`relative flex flex-col items-center text-center ${className}`}>
      <ResponsiveImage
        alt={`${user.nickname} avatar`}
        className="bg-gradient-purple-light-top p-0.5 rounded-full"
        height={114}
        imageClassName="rounded-full bg-white"
        srcKey={user.avatarPngUrl || defaultAvatarPng}
        width={114}
      />
      <p
        className={`flex justify-center items-center bg-purple-dark-top rounded-full ${
          rank === 1 ? 'w-6 h-6 -mt-4 pt-0.5' : 'w-4 h-4 -mt-3'
        }`}
      >
        {rank}
      </p>
      <h2 className={`font-semibold text-xs w-19 min-h-2 truncate ${rank === 1 ? 'w-19' : 'px-1.5 w-15'}`}>
        {user.nickname}
      </h2>
      <p className="text-3 whitespace-nowrap">{user.xp} XP</p>
    </div>
  )
}

export default function LeagueContainer(): React.ReactElement {
  const currentUser = useCurrentUser()
  const authenticated = useAppSelector(isAuthenticated)
  // See Unranked.tsx — teachers are never boarded, so they must not be told to earn XP.
  const isSchoolTeacher = useAppSelector(selectUserIsSchoolTeacher)
  const { data } = useGetLeaderboardDataQuery(undefined, { skip: !authenticated })
  const { t } = useTranslation('dashboard')
  const defaultAvatarPng = DEFAULT_AVATAR_URL
  const currentLeague = data?.currentLeague || 0
  const currentLeagueName = t(`leaderboard:league.${getLeagueName(currentLeague)}`)

  const dataOrdered = data?.users
    ? // eslint-disable-next-line fp/no-mutating-methods
      Array.from(data.users)
        .slice()
        .sort((a, b) => b.xp - a.xp)
        .map((leaderboardUser) => ({
          id: leaderboardUser.id,
          nickname: leaderboardUser.name,
          avatarPngUrl: leaderboardUser.avatar || '',
          xp: leaderboardUser.xp,
        }))
    : undefined

  const firstPlaceUser = dataOrdered ? dataOrdered[0] : null
  const secondPlaceUser = dataOrdered ? dataOrdered[1] : null
  const thirdPlaceUser = dataOrdered ? dataOrdered[2] : null

  const isCurrentUserInTopThree =
    dataOrdered && currentUser
      ? dataOrdered.slice(0, 3).some((leaderboardUser) => leaderboardUser.id === currentUser.id)
      : false

  const isUnranked = !dataOrdered || dataOrdered.length === 0 || !data?.userWantsToBeInLeaderboard

  if (isUnranked) {
    return (
      <div className="relative flex flex-col items-center justify-between py-8 gap-3">
        <ResponsiveImage height={64} srcKey="gamification/badges/league/bronze-badge.svg" width={64} />
        <div className="relative bg-white-whisper border border-white-veil rounded-large px-8 lg:px-4 py-4 mx-4 flex flex-col backdrop-blur-none">
          <h2 className="text-center">
            {isSchoolTeacher ? t('leaderboard:teacherComingSoon') : t('leaderboard:joinLeaderboard')}
          </h2>
        </div>
        <Button
          className="mx-auto bg-purple w-70 lg:w-60 h-11 shadow-purple-drop-glow !rounded-full font-bold text-xs"
          href={ROUTES.LEADERBOARD}
        >
          {t('checklists.exploreLeaderboard')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <h2 className="z-10 text-xl font-semibold text-center pt-2.5">{currentLeagueName}</h2>
      <div className="relative flex flex-col items-center -mt-1 h-56">
        <ResponsiveImage
          alt="Leaderboard background"
          className={`absolute left-0 right-0 ${
            isCurrentUserInTopThree ? '-mt-6' : '-mt-16'
          } mx-auto flex justify-center object-contain w-54`}
          height={500}
          srcKey="bootcamp/reward-chest-background.svg"
          width={500}
        />
        <div
          className={`flex w-full md:w-3/5 lg:w-full h-48 justify-center py-2 ${isCurrentUserInTopThree ? 'my-5' : ''}`}
        >
          <div className="w-15 mr-1.5 mt-12">
            {secondPlaceUser && <UserPortrait className="text-xxs" rank={2} user={secondPlaceUser} />}
          </div>
          <div className="w-19 mt-4">{firstPlaceUser && <UserPortrait rank={1} user={firstPlaceUser} />}</div>
          <div className="w-15 ml-1.5 mt-12">
            {thirdPlaceUser && <UserPortrait className="text-xxs" rank={3} user={thirdPlaceUser} />}
          </div>
        </div>

        <div className="z-10 w-full mb-3">
          {!isCurrentUserInTopThree &&
            dataOrdered &&
            currentUser &&
            (() => {
              const currentUserData = dataOrdered.find((leaderboardUser) => leaderboardUser.id === currentUser.id)
              if (!currentUserData) return null

              const currentUserRank =
                dataOrdered.findIndex((leaderboardUser) => leaderboardUser.id === currentUser.id) + 1

              return (
                <div
                  className="flex h-8 mx-auto w-62 lg::w-56 px-2 rounded-full bg-gradient-purple-light-top font-semibold"
                  key={currentUserData.id}
                >
                  <div className="flex flex-col justify-center ml-3">
                    <span className="relative z-10">{currentUserRank}</span>
                  </div>
                  <div className="flex w-50 mx-2 justify-center items-center">
                    <ResponsiveImage
                      alt={`${currentUserData.nickname} avatar`}
                      className="mx-1"
                      height={40}
                      imageClassName="w-8 h-7 rounded-full my-0.5 bg-white"
                      srcKey={currentUserData.avatarPngUrl || defaultAvatarPng}
                      width={40}
                    />
                    <h2 className="text-md w-30 truncate pl-2">{currentUserData.nickname}</h2>
                  </div>
                  <p className="flex flex-col justify-center mr-3">{currentUserData.xp}</p>
                </div>
              )
            })()}
        </div>
      </div>
      <Button
        className="bg-purple mx-auto w-60 lg:w-57 h-11 shadow-purple-drop-glow !rounded-full font-bold text-xs"
        href={ROUTES.LEADERBOARD}
      >
        {t('checklists.exploreLeaderboard')}
      </Button>
    </div>
  )
}
